// @flow
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import FlightIcon from '@material-ui/icons/Flight';
import Box from "@material-ui/core/Box";
import List from '@material-ui/core/List';
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Chip from '@material-ui/core/Chip';
import Typography from "@material-ui/core/Typography";
import type { Car, Plane, User } from '../../types';
import { TYPE } from '../../constants/targetConstants';
import mapService from '../../services/map';
import Status from '../ui/Status';
import Header from '../Sidebar/Header';
import Messages from '../Messages';
import Row from './Row';

const isNumber = (value) => !isNaN(parseFloat(value));
const toNumber = (fractionDigits = 0) => (value) => (isNumber(value) ? parseFloat(value).toFixed(fractionDigits) : undefined);
const padNumber = (value) => (isNumber(value) ? ('00' + toNumber()(value)).slice(-3) : undefined);

const useStyles = makeStyles(theme => ({
    listItemIcon: {
        disaply: 'block',
        color: theme.palette.text.primary
    },
    list: {
        backgroundColor: theme.palette.grey[300],
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    chip: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
        fontSize: 'inherit',
        height: 'auto',
        marginLeft: '1ch',
        verticalAlign: 'baseline'
    }
}));

const getIcon = (type) => {
    if (type === TYPE.car) {
        return <DirectionsCarIcon fontSize="large"/>;
    } else if (type === TYPE.plane) {
        return <FlightIcon fontSize="large"/>;
    } else {
        throw new Error(`Unknown target type ${type}`);
    }
};

export type Props = $ReadOnly<{
    target: Car | Plane,
    callsign?: string,
    headingComputed?: boolean,
    heading?: number,
    speedComputed?: boolean,
    speed?: number,
    operator?: User
}>;

function TargetInspector({ target, callsign, headingComputed, heading, speedComputed, speed, operator }: Props) {
    const classes = useStyles();

    useEffect(() => {
        return () => {
            mapService.deselectAll('target');
        };
    }, []);

    if (!target) {
        return null;
    }

    return (
        <List subheader={(
            <Header
                title={(
                    <Box fontSize="h6.fontSize">
                        <FormattedMessage
                            id="sidebar.targetInspector.header"
                            defaultMessage="Target Inspector"
                        />
                    </Box>
                )}
            />
        )}>
            <Box m={2}>
                <Status date={target.TimeOfTrackInformation}/>
                <FormattedMessage
                    id="sidebar.targetInspector.lastUpdate"
                    defaultMessage="Last update"
                />: {new Date(target.TimeOfTrackInformation).toLocaleTimeString()}
            </Box>
            <List dense={true} className={classes.list}>
                <ListItem>
                    <ListItemIcon classes={{ root: classes.listItemIcon }}>
                        {getIcon(target.type)}
                    </ListItemIcon>
                    <ListItemText
                        primary={(
                            <React.Fragment>
                                <Typography variant="h6" component="div">
                                    <FormattedMessage
                                        id="targetInspector.callsign"
                                        defaultMessage="Callsign"
                                    />:
                                    <Chip className={classes.chip} size="small" label={callsign}/>
                                </Typography>
                            </React.Fragment>
                        )}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon><span /></ListItemIcon>
                    <ListItemText
                        primary={(
                            <Typography variant="subtitle1" component="div">
                                <FormattedMessage
                                    id="targetInspector.icao"
                                    defaultMessage="ICAO"
                                />: {target.Icao}
                            </Typography>
                        )}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon><span /></ListItemIcon>
                    <ListItemText
                        primary={(
                            <Typography variant="subtitle1" component="div">
                                <FormattedMessage
                                    id="targetInspector.operator"
                                    defaultMessage="Operator"
                                />: {operator ?
                                    operator.name ?
                                        operator.name
                                        :
                                        <FormattedMessage
                                            id="targetInspector.unknownName"
                                            defaultMessage="Unknown name"
                                        />
                                    :
                                    <FormattedMessage
                                        id="general.na"
                                        defaultMessage="N/A"
                                    />
                                }
                            </Typography>
                        )}
                    />
                </ListItem>
            </List>
            <Box m={2}>
                <Table>
                    <TableBody>
                        <Row
                            target={target}
                            cells={[
                                {
                                    id: 'EmitterCategory',
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.type"
                                            defaultMessage="Type"
                                        />
                                    )
                                },
                                {
                                    id: null,
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.call"
                                            defaultMessage="CALL"
                                        />
                                    )
                                }
                            ]}
                        />
                        <Row
                            target={target}
                            cells={[
                                {
                                    id: 'Squawk',
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.squawk"
                                            defaultMessage="SQUAWK"
                                        />
                                    )
                                },
                                {
                                    value: speed,
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.velh"
                                            defaultMessage="VELH"
                                        />
                                    ),
                                    valueColor: speedComputed ? 'blue' : undefined,
                                    parser: toNumber()
                                }
                            ]}
                        />
                        <Row
                            target={target}
                            cells={[
                                {
                                    value: heading,
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.heading"
                                            defaultMessage="Heading (deg)"
                                        />
                                    ),
                                    valueColor: headingComputed ? 'blue' : undefined,
                                    parser: padNumber
                                },
                                {
                                    id: ['MovementData', 'VerticalSpeed'],
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.velv"
                                            defaultMessage="VELV"
                                        />
                                    ),
                                    parser: toNumber()
                                }
                            ]}
                        />
                        <Row
                            target={target}
                            cells={[
                                {
                                    id: ['MovementData', 'Position', 0],
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.lat"
                                            defaultMessage="LAT"
                                        />
                                    ),
                                    parser: toNumber(6)
                                },
                                {
                                    id: null,
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.sigs"
                                            defaultMessage="SIGS"
                                        />
                                    )
                                }
                            ]}
                        />
                        <Row
                            target={target}
                            cells={[
                                {
                                    id: ['MovementData', 'Position', 1],
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.lng"
                                            defaultMessage="LNG"
                                        />
                                    ),
                                    parser: toNumber(6)
                                },
                                {
                                    id: null,
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.sigq"
                                            defaultMessage="SIGQ"
                                        />
                                    )
                                }
                            ]}
                        />
                        <Row
                            target={target}
                            cells={[
                                {
                                    id: ['MovementData', 'FlightLevel'],
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.lng"
                                            defaultMessage="Altitude (ft)"
                                        />
                                    ),
                                    parser: toNumber()
                                },
                                {
                                    id: null,
                                    label: (
                                        <FormattedMessage
                                            id="targetInspector.label.fps"
                                            defaultMessage="FPS"
                                        />
                                    )
                                }
                            ]}
                        />
                    </TableBody>
                </Table>
            </Box>

            {target.type === TYPE.car &&
                <Messages icao={target.Icao} userId={operator ? operator.uid : undefined} />
            }
        </List>
    );
}

export default TargetInspector;